import React, { useState } from 'react';
import { Box, Typography, Paper, Grid, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { getTypeDescription, PersonalityType } from '../data/mbtiTypes';
import AdSense from './AdSense';
import '../styles/Result.css';

interface ResultProps {
  type: PersonalityType;
  catName: string;
  onRestart: () => void;
}

const Result: React.FC<ResultProps> = ({ type, catName, onRestart }) => {
  const personality = getTypeDescription(type);
  const [copyLabel, setCopyLabel] = useState('複製連結');

  const shareText = `我家${catName}是${type}！快來測測你的貓咪 🐱`;
  const shareUrl = 'https://maombti.smallfatmao.com';
  const lineShareUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopyLabel('✓ 已複製！');
      setTimeout(() => setCopyLabel('複製連結'), 1500);
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="result-container"
    >
      <Box className="result-content">
        <Typography variant="h4" className="result-title">
          {catName} 的貓咪性格分析
        </Typography>
        
        <Typography variant="h5" className="result-subtitle">
          {catName} 的 MBTI 類型是 {type} ({personality.description.split('，')[0].replace('你是一隻', '').replace('的貓咪', '').split('。')[0].replace('又', '').replace('且', '').replace('並', '').replace('，', '')})的貓
        </Typography>

        <Typography variant="body1" className="result-description">
          {personality.description}
        </Typography>

        <Grid container spacing={3} className="result-grid">
          <Grid item xs={12} md={6}>
            <Paper className="trait-card">
              <Typography variant="h6" className="card-title">
                性格特質
              </Typography>
              <ul className="trait-list">
                {personality.traits.map((trait, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="trait-item"
                  >
                    {trait}
                  </motion.li>
                ))}
              </ul>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper className="suggestion-card">
              <Typography variant="h6" className="card-title">
                相處建議
              </Typography>
              <ul className="suggestion-list">
                {personality.tips.map((tip, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="suggestion-item"
                  >
                    {tip}
                  </motion.li>
                ))}
              </ul>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper className="dialogue-card">
              <Typography variant="h6" className="card-title">
                貓咪想說的話
              </Typography>
              <ul className="dialogue-list">
                {personality.dialogue.map((dialogue, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="dialogue-item"
                  >
                    {dialogue}
                  </motion.li>
                ))}
              </ul>
            </Paper>
          </Grid>
        </Grid>

        {/* 廣告區塊 */}
        <Box sx={{ width: '100%', my: 3 }}>
          <AdSense slot="4858617227" />
        </Box>

        {/* 分享按鈕組 */}
        <Box sx={{
          display: 'flex',
          gap: '12px',
          mb: 2,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          <Button
            variant="contained"
            component="a"
            href={lineShareUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              background: '#06C755',
              color: '#fff',
              height: 48,
              px: 3,
              fontWeight: 700,
              fontSize: 15,
              borderRadius: 2,
              '&:hover': { background: '#05a847' },
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
            }}
          >
            分享到 LINE
          </Button>
          <Button
            variant="outlined"
            onClick={handleCopy}
            sx={{
              color: '#2D1B10',
              borderColor: '#2D1B10',
              height: 48,
              px: 3,
              fontWeight: 700,
              fontSize: 15,
              borderRadius: 2,
              '&:hover': { borderColor: '#4A2D1A', background: 'rgba(45,27,16,0.04)' },
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              minWidth: 130,
            }}
          >
            {copyLabel}
          </Button>
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={onRestart}
          className="restart-button"
        >
          重新測驗
        </Button>

        {/* 導流 MaoMoment */}
        <Box
          component="a"
          href="https://moment.smallfatmao.com"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mt: 4,
            p: '16px 20px',
            borderRadius: 3,
            background: 'rgba(245,166,35,0.08)',
            border: '1px solid rgba(245,166,35,0.3)',
            textDecoration: 'none',
            transition: 'background 0.2s',
            '&:hover': { background: 'rgba(245,166,35,0.14)' },
          }}
        >
          <span style={{ fontSize: 28 }}>📸</span>
          <Box>
            <Box sx={{ fontWeight: 700, fontSize: 14, color: '#2D1B10', fontFamily: '"Nunito", sans-serif' }}>
              辦活動也想留住大家的照片？
            </Box>
            <Box sx={{ fontSize: 13, color: '#6B4C3B', mt: 0.3, fontFamily: '"Nunito", sans-serif' }}>
              試試 MaoMoment — 賓客掃 QR 即時上傳，免費、免 App →
            </Box>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
};

export default Result; 